/**
 * API Service for Agrotrack Dashboard
 * 
 * Handles all API requests with error handling, retries, and caching.
 */

import { API_CONFIG } from '../config/api.config';

class APIService {
  constructor() {
    this.baseURL = API_CONFIG.baseURL;
    this.timeout = API_CONFIG.timeout;
    this.retries = API_CONFIG.retries;
    this.retryDelay = API_CONFIG.retryDelay;
    this.cache = new Map();
  }

  /**
   * Make HTTP request with retry logic
   */
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      method: options.method || 'GET',
      headers: {
        ...API_CONFIG.headers,
        ...options.headers,
      },
      ...options,
    };

    if (config.body && typeof config.body === 'object') {
      config.body = JSON.stringify(config.body);
    }

    let lastError;
    for (let attempt = 0; attempt < this.retries; attempt++) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), this.timeout);

        const response = await fetch(url, {
          ...config,
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        return data;
      } catch (error) {
        lastError = error;
        if (attempt < this.retries - 1) {
          await new Promise(resolve => setTimeout(resolve, this.retryDelay * Math.pow(2, attempt)));
        }
      }
    }

    throw lastError;
  }

  /**
   * GET request
   */
  async get(endpoint, options = {}) {
    const cacheKey = endpoint + JSON.stringify(options);

    if (this.cache.has(cacheKey) && !options.noCache) {
      return this.cache.get(cacheKey);
    }

    const data = await this.request(endpoint, { ...options, method: 'GET' });

    if (!options.noCache) {
      this.cache.set(cacheKey, data);
      setTimeout(() => this.cache.delete(cacheKey), 5 * 60 * 1000); // 5 min cache
    }

    return data;
  }

  /**
   * POST request
   */
  async post(endpoint, body, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'POST',
      body,
    });
  }

  /**
   * Health check
   */
  async checkHealth() {
    return this.get(API_CONFIG.endpoints.health);
  }

  /**
   * Get single prediction
   */
  async getPrediction(features, modelName = null) {
    return this.post(API_CONFIG.endpoints.predict, {
      features,
      model_name: modelName,
    });
  }

  /**
   * Get batch predictions
   */
  async getBatchPredictions(featuresList, modelName = null) {
    return this.post(API_CONFIG.endpoints.predictBatch, {
      features: featuresList,
      model_name: modelName,
    });
  }

  /**
   * List all models
   */
  async listModels() {
    return this.get(API_CONFIG.endpoints.models);
  }

  /**
   * Get model details
   */
  async getModelDetails(modelName) {
    return this.get(API_CONFIG.endpoints.modelDetail(modelName));
  }

  /**
   * Get model metrics
   */
  async getModelMetrics(modelName) {
    return this.get(API_CONFIG.endpoints.modelMetrics(modelName));
  }

  /**
   * Compare models
   */
  async compareModels() {
    return this.get(API_CONFIG.endpoints.modelComparison);
  }

  /**
   * Clear cache
   */
  clearCache() {
    this.cache.clear();
  }
}

const apiService = new APIService();
export default apiService;
